import React, { useContext, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Layout, Avatar, Typography } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  UserAddOutlined,
  BugOutlined,
  InfoCircleOutlined,
  HeartOutlined,
  ExportOutlined
} from '@ant-design/icons';
import Logo from '../../assets/images/logo512.png';
import {
  HOME_ROUTE,
  ABOUT_ROUTE,
  LOGIN_ROUTE,
  DASHBOARD_ROUTE,
  PROBLEM_SETS_ROUTE,
  SIGN_UP_ROUTE,
  TURKEY_SYRIA_IRC_DONATION_SHOW,
  TURKEY_SYRIA_IRC_DONATION_URL,
  PALESTINE_IRC_DONATION_SHOW,
  PALESTINE_IRC_DONATION_URL,
  PALESTINE_PCRF_DONATION_SHOW,
  PALESTINE_PCRF_DONATION_URL,
  FOUNDING_YEAR,
  PRIVACY_POLICY_ROUTE,
  COOKIES_POLICY_ROUTE,
  TERMS_AND_CONDITIONS_ROUTE,
  SETTINGS_ROUTE,
  BANGLADESH_BANNER_SHOW,
  BANGLADESH_ALJAZEERA_LINK,
  PALESTINE_BANNER_SHOW,
  PALESTINE_BANNER_LINK
 } from '../../config/constants';
import { AuthContext, AuthContextState } from '../../context/AuthContext';
import { getUserAvatarSrc } from '../../utils/accounts';
import { getPalestineDatasetsSummary, SummaryData } from '../../utils/palestineDatasets';
import { formatNumberWithCommas } from '../../utils/misc';
import styles from './MainLayout.module.css';

export interface MainLayoutProps {
  children?: JSX.Element | JSX.Element[]
  // key of currently selected page
  selectedKey?: string | undefined
  // width of content
  contentWidth?: number
};

// takes page content and displays it along with
// navbar on top and footer at bottom
const MainLayout = (props: MainLayoutProps): JSX.Element | null => {
  // get currently authenticated state from auth context
  const { authState, me, logoutUser } = useContext(AuthContext) as AuthContextState;
  const navigate = useNavigate();
  const [palestiniansKilled, setPalestiniansKilled] = useState<string>('thousands of');
  const [palestineChildrenKilled, setPalestinianChildrenKilled] = useState<string>('nearly half');

  useEffect(() => {
    getPalestineDatasetsSummary().then(
      (response: AxiosResponse) => response.data as SummaryData
    ).then((summaryData: SummaryData) => {
      setPalestiniansKilled(formatNumberWithCommas(summaryData.gaza.killed.total + summaryData.west_bank.killed.total));
      setPalestinianChildrenKilled(formatNumberWithCommas(summaryData.gaza.killed.children + summaryData.west_bank.killed.children));
    }).catch((error: any) => {});
  }, [setPalestiniansKilled]);

  const handleLogout = (e: React.MouseEvent<HTMLElement>): void => {
    logoutUser();
    navigate(
      LOGIN_ROUTE,
      {
        state: {
          statusMessages: [
            {
              message: 'Logged out successfully',
              status: 'success'
            },
          ]
        }
      }
    );
  };

  return (
    <Layout className={styles.layout}>
      <Layout.Header className={styles.navbarLayout}>
        <div className={styles.paddedContainer}>
          <div key='logo' className={styles.logo}>
            <Link to={authState ? DASHBOARD_ROUTE : HOME_ROUTE}>
              <img alt="Sigmoid Academy" src={Logo} className={styles.logoImg}/>
            </Link>
          </div>
          {authState &&
            <div key='dashboard' className={styles.navbarItemContainer}>
              <Link to={DASHBOARD_ROUTE}>
                <span className={props.selectedKey === 'dashboard' ? styles.navbarItemSelected : styles.navbarItem}>
                  Dashboard
                </span>
              </Link>
            </div>
          }
          {authState &&
            <div key='dashboard-mobile' className={styles.navbarMobileItemContainer}>
              <Link to={DASHBOARD_ROUTE}>
                <DashboardOutlined
                  className={props.selectedKey === 'dashboard' ? styles.navbarMobileItemSelected : styles.navbarMobileItem}
                />
              </Link>
            </div>
          }
          {!authState &&
            <div key='login' className={styles.navbarItemContainer}>
              <Link to={LOGIN_ROUTE}>
                <span className={props.selectedKey === 'login' ? styles.navbarItemSelected : styles.navbarItem}>
                  Login
                </span>
              </Link>
            </div>
          }
          {!authState &&
            <div key='login-mobile' className={styles.navbarMobileItemContainer}>
              <Link to={LOGIN_ROUTE}>
                <UserOutlined
                  className={props.selectedKey === 'login' ? styles.navbarMobileItemSelected : styles.navbarMobileItem}
                />
              </Link>
            </div>
          }
          {!authState &&
            <div key='signup' className={styles.navbarItemContainer}>
              <Link to={SIGN_UP_ROUTE}>
                <span className={props.selectedKey === 'signup' ? styles.navbarItemSelected : styles.navbarItem}>
                  Sign Up
                </span>
              </Link>
            </div>
          }
          {!authState &&
            <div key='signup-mobile' className={styles.navbarMobileItemContainer}>
              <Link to={SIGN_UP_ROUTE}>
                <UserAddOutlined
                  className={props.selectedKey === 'signup' ? styles.navbarMobileItemSelected : styles.navbarMobileItem}
                />
              </Link>
            </div>
          }
          <div key='problem-sets' className={styles.navbarItemContainer}>
            <Link to={PROBLEM_SETS_ROUTE}>
              <span className={props.selectedKey === 'problem-sets' ? styles.navbarItemSelected : styles.navbarItem}>
                Problem Sets
              </span>
            </Link>
          </div>
          <div key='problem-sets-mobile' className={styles.navbarMobileItemContainer}>
            <Link to={PROBLEM_SETS_ROUTE}>
              <BugOutlined
                className={props.selectedKey === 'problem-sets' ? styles.navbarMobileItemSelected : styles.navbarMobileItem}
              />
            </Link>
          </div>
          <div key='about' className={styles.navbarItemContainer}>
            <Link to={ABOUT_ROUTE}>
              <span className={props.selectedKey === 'about' ? styles.navbarItemSelected : styles.navbarItem}>
                About
              </span>
            </Link>
          </div>
          <div key='about-mobile' className={styles.navbarMobileItemContainer}>
            <Link to={ABOUT_ROUTE}>
              <InfoCircleOutlined
                className={props.selectedKey === 'about' ? styles.navbarMobileItemSelected : styles.navbarMobileItem}
              />
            </Link>
          </div>
          {TURKEY_SYRIA_IRC_DONATION_SHOW &&
            <div key='support-turkey-syria-irc' className={styles.navbarItemContainer}>
              <a href={TURKEY_SYRIA_IRC_DONATION_URL} target='_blank' rel='noopener noreferrer'>
                <span className={styles.navbarItem}>
                  Support Turkey & Syria
                </span>
              </a>
            </div>
          }
          {TURKEY_SYRIA_IRC_DONATION_SHOW &&
            <div key='support-turkey-syria-irc-mobile' className={styles.navbarMobileItemContainer}>
              <a href={TURKEY_SYRIA_IRC_DONATION_URL} target='_blank' rel='noopener noreferrer'>
                <HeartOutlined className={styles.navbarMobileItem}/>
              </a>
            </div>
          }
          {PALESTINE_IRC_DONATION_SHOW &&
            <div key='support-palestine-irc' className={styles.navbarItemContainer}>
              <a href={PALESTINE_IRC_DONATION_URL} target='_blank' rel='noopener noreferrer'>
                <span className={styles.navbarItem}>
                  Support Palestine
                </span>
              </a>
            </div>
          }
          {PALESTINE_IRC_DONATION_SHOW &&
            <div key='support-palestine-irc-mobile' className={styles.navbarMobileItemContainer}>
              <a href={PALESTINE_IRC_DONATION_URL} target='_blank' rel='noopener noreferrer'>
              <HeartOutlined className={styles.navbarMobileItem}/>
              </a>
            </div>
          }
          {PALESTINE_PCRF_DONATION_SHOW &&
            <div key='support-palestine-pcrf' className={styles.navbarItemContainer}>
              <a href={PALESTINE_PCRF_DONATION_URL} target='_blank' rel='noopener noreferrer'>
                <span className={styles.navbarItem}>
                  Support Palestine
                </span>
              </a>
            </div>
          }
          {PALESTINE_PCRF_DONATION_SHOW &&
            <div key='support-palestine-pcrf-mobile' className={styles.navbarMobileItemContainer}>
              <a href={PALESTINE_PCRF_DONATION_URL} target='_blank' rel='noopener noreferrer'>
              <HeartOutlined className={styles.navbarMobileItem}/>
              </a>
            </div>
          }
          <div key='rightnavbar' className={styles.rightNavbarItemGroup}>
            {authState &&
              <div key='logout' className={styles.navbarItemContainer}>
                <span style={{cursor: 'pointer'}} onClick={handleLogout} className={props.selectedKey === 'logout' ? styles.navbarItemSelected : styles.navbarItem}>
                  Logout
                </span>
              </div>
            }
            {authState &&
              <div key='logout-mobile' className={styles.navbarMobileItemContainer}>
                <ExportOutlined
                  onClick={handleLogout}
                  className={props.selectedKey === 'logout' ? styles.navbarMobileItemSelected : styles.navbarMobileItem}
                />
              </div>
            }
            {me &&
              <div key='username' className={styles.navbarItemContainer}>
                <Link to={SETTINGS_ROUTE}>
                  <span className={props.selectedKey === 'username' ? styles.navbarItemSelected : styles.navbarItem}>
                    {me.display_username}
                  </span>
                </Link>
              </div>
            }
            {me &&
              <div key='avatar' className={styles.navbarItemContainer}>
                <Link to={SETTINGS_ROUTE}>
                  <Avatar src={getUserAvatarSrc(me.display_username)} />
                </Link>
              </div>
            }
            {me &&
              <div key='avatar-mobile' className={styles.navbarMobileItemContainer}>
                <Link to={SETTINGS_ROUTE}>
                  <Avatar
                    size={'small'}
                    src={getUserAvatarSrc(me.display_username)}
                  />
                </Link>
              </div>
            }
          </div>
        </div>
      </Layout.Header>
      {BANGLADESH_BANNER_SHOW &&
        <Layout.Header
          className={`${styles.bannerLayout} ${styles.bangladeshBannerLayout}`}
          onClick={(e: React.MouseEvent) => {
            window.open(BANGLADESH_ALJAZEERA_LINK, '_blank');
          }}
        >
          <Typography.Text
            className={`${styles.bannerText} ${styles.bangladeshBannerText}`}
            strong={true}
          >
            Sheikh Hasina's fascist government has <span style={{color: '#F42A41'}}>murdered</span> hundreds of Bangladeshi students for peacefully protesting.
          </Typography.Text>
        </Layout.Header>
      }
      {PALESTINE_BANNER_SHOW &&
        <Layout.Header
          className={`${styles.bannerLayout} ${styles.palestineBannerLayout}`}
          onClick={(e: React.MouseEvent) => {
            window.open(PALESTINE_BANNER_LINK, '_blank');
          }}
        >
          <Typography.Text
            className={`${styles.bannerText} ${styles.palestineBannerText}`}
            strong={true}
          >
            Since October 7, 2023, Israel has murdered <span style={{color: '#F42A41'}}>{palestiniansKilled}</span> Palestinians, out of whom, <span style={{color: '#F42A41'}}>{palestineChildrenKilled}</span> were children. Please consider donating to <span style={{color: '#B3CCFF'}}><u>Palestine Children's Relief Fund</u></span>.
          </Typography.Text>
        </Layout.Header>
      }
      <Layout.Content className={styles.content}>
        <div style={{width: props.contentWidth ? `${props.contentWidth}%` : '85%'}}>
          {props.children}
        </div>
      </Layout.Content>
      <Layout.Footer className={styles.footer}>
        <div className={styles.footerMainContainer}>
          <Link to={PRIVACY_POLICY_ROUTE}>
            <Typography.Text
              className={styles.footerTextLink}
            >
              Privacy Policy
            </Typography.Text>
          </Link>
          <Link to={COOKIES_POLICY_ROUTE}>
            <Typography.Text
              className={styles.footerTextLink}
            >
              Cookies Policy
            </Typography.Text>
          </Link>
          <Link to={TERMS_AND_CONDITIONS_ROUTE}>
            <Typography.Text
              className={styles.footerTextLink}
            >
              Terms & Conditions
            </Typography.Text>
          </Link>
          <Typography.Text
            className={styles.footerText}
          >
            {`Sigmoid Academy © ${FOUNDING_YEAR} - ${new Date().getFullYear()}`}
          </Typography.Text>
        </div>
      </Layout.Footer>
    </Layout>
  );
};

export default MainLayout;
